// Cupom do Novo Pedido do Delivery conferido de novo (30/09/2026). Antes o
// cupom era validado uma vez só, no "Aplicar": mudou o carrinho, a taxa ou o
// cliente, o desconto ficava congelado (percentual sobre o subtotal antigo,
// frete grátis com a taxa antiga, cupom de outro cliente). Agora, 1 s depois
// da última mudança, valida de novo — a mesma ideia do Balcão
// (features/totem/hooks/useDescontoBalcao.js). Deixou de valer → sai e avisa.
import axios from "axios";
import { useCallback, useEffect, useRef } from "react";

const ESPERA_MS = 1000;
const centavos = (v) => Math.round((Number(v) || 0) * 100);

/** Desconto do cupom na conta da tela. Frete grátis nunca passa da taxa atual. */
export function descontoDoCupom(cupom, subtotal, taxa) {
  const valor = Math.max(0, Math.min(subtotal + taxa, Number(cupom?.discount || 0)));
  return cupom?.coupon?.discount_type === "free_delivery" ? Math.min(valor, taxa) : valor;
}

async function validar({ codigo, subtotal, taxa, cliente, itens }) {
  const { data } = await axios.post("/api/coupons/validate", {
    code: codigo,
    order_total: subtotal + taxa,
    products_total: subtotal,
    delivery_fee: taxa,
    customer_id: cliente?.id || null,
    customer_phone: cliente?.telefone || null,
    // Com os itens, o servidor tira da conta o produto em promoção que não aceita cupom (achado 411).
    ...(itens?.length ? { items: itens, modulo: "Delivery" } : {}),
  });
  return data || {};
}

/**
 * `codigo`: o cupom aplicado (ou null). `desconto`: o valor que a tela usa.
 * `aplicar(resposta)` grava o cupom revalidado; `remover(motivo)` tira o cupom.
 * `editando`: pedido já gravado — o cupom já foi aceito nele (o próprio pedido
 * conta no limite de uso), então cupom que "não vale" continua como estava.
 * Sem cliente escolhido, espera o cliente para conferir.
 * Devolve `conferirAgora()`, usado no Criar: `true` = nada mudou, pode gravar.
 */
export function useRevalidarCupom({ codigo, desconto, subtotal, taxa, cliente, itens, editando, aplicar, remover }) {
  const chave = codigo && cliente?.id ? `${codigo}|${centavos(subtotal)}|${centavos(taxa)}|${cliente.id}` : null;
  const atual = useRef({});
  atual.current = { codigo, desconto, subtotal, taxa, cliente, itens, editando, aplicar, remover, chave };
  const conferida = useRef(null); // chave já conferida
  const codigoVisto = useRef(null);

  const conferir = useCallback(async () => {
    const a = atual.current;
    if (!a.chave) return true;
    let d;
    try {
      d = await validar(a);
    } catch {
      return true; // sem rede: fica o valor anterior; o servidor confere ao gravar
    }
    if (atual.current.chave !== a.chave) return false; // mudou de novo no meio: outra conferência vem
    conferida.current = a.chave;
    if (d.valid) {
      a.aplicar(d);
      return centavos(d.discount) === centavos(a.desconto);
    }
    if (a.editando) return true;
    a.remover(d.message || d.error || "não vale mais para este pedido");
    return false;
  }, []);

  useEffect(() => {
    // Cupom acabou de entrar (Aplicar, Balcão, edição): foi validado agora.
    if (codigo !== codigoVisto.current) {
      codigoVisto.current = codigo;
      conferida.current = chave;
      return undefined;
    }
    if (!chave || chave === conferida.current) return undefined;
    const t = setTimeout(conferir, ESPERA_MS);
    return () => clearTimeout(t);
  }, [codigo, chave, conferir]);

  const conferirAgora = useCallback(async () => {
    const a = atual.current;
    if (!a.chave || a.chave === conferida.current) return true;
    return conferir();
  }, [conferir]);

  return { conferirAgora };
}
